import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Globe, ChevronDown, User, LogOut, LayoutDashboard } from "lucide-react";
import Logo from "./Logo";
import { useAuth, isStaffUser } from "../context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const NAV_TABS = [
  { to: "/", label: "Home" },
  { to: "/immigration", label: "Immigration" },
  { to: "/documents-identity", label: "Documents & Identity" },
  { to: "/itin-tax", label: "ITIN & Tax" },
  { to: "/screening", label: "Screening" },
  { to: "/new-life", label: "New Life" },
  { to: "/citizenship", label: "Citizenship" },
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

const SERVICE_LINKS = [
  { to: "/immigration", label: "Immigration" },
  { to: "/documents-identity", label: "Documents & Identity" },
  { to: "/itin-tax", label: "ITIN & Tax" },
  { to: "/screening", label: "Screening & Verification" },
  { to: "/new-life", label: "New Life Services" },
  { to: "/citizenship", label: "Citizenship" },
];

export default function SiteHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-ivory/90 border-b border-line" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" aria-label="CF Full Circle home">
            <Logo size={44} />
          </Link>

          <div className="hidden lg:flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm text-mutedink hover:text-teal transition-colors" data-testid="language-selector">
                <Globe className="h-4 w-4" /> English <ChevronDown className="h-3 w-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-surface border-line">
                <DropdownMenuItem data-testid="language-option-en">English</DropdownMenuItem>
                <DropdownMenuItem disabled className="text-mutedink">Español — coming soon</DropdownMenuItem>
                <DropdownMenuItem disabled className="text-mutedink">Kreyòl — coming soon</DropdownMenuItem>
                <DropdownMenuItem disabled className="text-mutedink">Português — coming soon</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 text-sm font-medium text-cream hover:text-teal transition-colors" data-testid="user-menu-button">
                  <User className="h-4 w-4" /> {user.name?.split(" ")[0] || "Account"}
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-surface border-line">
                  <DropdownMenuItem asChild>
                    <Link to={isStaffUser(user) ? "/staff" : "/portal"} data-testid="user-menu-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> {isStaffUser(user) ? "Staff Dashboard" : "My Portal"}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="user-menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="text-sm font-medium text-cream hover:text-teal transition-colors" data-testid="client-login-link">
                Client Login
              </Link>
            )}
            <Button asChild className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full px-6" data-testid="header-start-journey-button">
              <Link to="/start">Start Your Journey</Link>
            </Button>
          </div>

          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="p-2 text-cream" aria-label="Open menu" data-testid="mobile-menu-button">
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-ivory w-80">
                <div className="mt-8 flex flex-col gap-5">
                  {SERVICE_LINKS.map((l) => (
                    <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-base font-medium text-cream" data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      {l.label}
                    </Link>
                  ))}
                  <Link to="/about" onClick={() => setOpen(false)} className="text-base font-medium text-cream" data-testid="mobile-nav-about">About</Link>
                  <Link to="/resources" onClick={() => setOpen(false)} className="text-base font-medium text-cream" data-testid="mobile-nav-resources">Resources</Link>
                  <Link to="/contact" onClick={() => setOpen(false)} className="text-base font-medium text-cream" data-testid="mobile-nav-contact">Contact</Link>
                  <div className="border-t border-line pt-5 flex flex-col gap-3">
                    {user ? (
                      <>
                        <Link to={isStaffUser(user) ? "/staff" : "/portal"} onClick={() => setOpen(false)} className="text-base font-medium text-teal" data-testid="mobile-nav-portal">
                          {isStaffUser(user) ? "Staff Dashboard" : "My Portal"}
                        </Link>
                        <button onClick={() => { handleLogout(); setOpen(false); }} className="text-left text-base font-medium text-cream" data-testid="mobile-nav-logout">Sign Out</button>
                      </>
                    ) : (
                      <Link to="/login" onClick={() => setOpen(false)} className="text-base font-medium text-teal" data-testid="mobile-nav-login">Client Login</Link>
                    )}
                    <Button asChild className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="mobile-start-journey-button">
                      <Link to="/start" onClick={() => setOpen(false)}>Start Your Journey</Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      <nav className="border-t border-line/60" aria-label="Site tabs" data-testid="nav-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-2" style={{ scrollbarWidth: "none" }}>
            {NAV_TABS.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.to === "/"}
                data-testid={`nav-tab-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "bg-teal text-white" : "text-cream/70 hover:text-white hover:bg-white/5"}`
                }
              >
                {t.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>
    </header>
  );
}
